import { useEffect, useState } from "react";
import { payments as api, invoices as invApi, apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wallet, Plus, Trash2 } from "lucide-react";

export default function Payments() {
  const [list, setList] = useState(null);
  const [invs, setInvs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ invoice_id: "", amount: 0, method: "EFT", reference: "", date: new Date().toISOString().slice(0, 10), notes: "" });

  const load = () => Promise.all([api.list(), invApi.list()]).then(([p, i]) => { setList(p); setInvs(i.filter((x) => x.display_status !== "paid" && x.display_status !== "draft")); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const pickInv = (iid) => { const inv = invs.find((x) => x.id === iid); setForm({ ...form, invoice_id: iid, amount: inv ? Math.max(inv.total - (inv.amount_paid || 0), 0) : 0 }); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.invoice_id) return toast.error("Select an invoice.");
    try { await api.create({ ...form, amount: Number(form.amount) }); toast.success("Payment recorded"); setOpen(false); setForm({ ...form, invoice_id: "", amount: 0, reference: "" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (id) => { try { await api.remove(id); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!list) return <Loading />;
  const total = list.reduce((a, p) => a + p.amount, 0);

  return (
    <div>
      <PageHeader title="Payments" subtitle="Record and track payments received.">
        <Button onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="record-payment-page"><Plus className="w-4 h-4 mr-1.5" /> Record Payment</Button>
      </PageHeader>

      <Card className="p-5 mb-4 flex items-center justify-between">
        <div><div className="text-xs text-slate-500 uppercase tracking-wide">Total Received</div><div className="font-display text-2xl font-bold text-emerald-600">{money(total)}</div></div>
        <div className="w-11 h-11 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
      </Card>

      {list.length === 0 ? (
        <EmptyState icon={Wallet} title="No payments yet" description="Record a payment against an invoice to track your cash flow." />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Invoice</th>
                <th className="px-5 py-3 font-semibold hidden md:table-cell">Customer</th>
                <th className="px-5 py-3 font-semibold hidden md:table-cell">Method / Ref</th>
                <th className="px-5 py-3 font-semibold text-right">Amount</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                  <td className="px-5 py-3.5 text-slate-600">{fmtDate(p.date)}</td>
                  <td className="px-5 py-3.5 font-semibold text-[#0B132B]">{p.invoice_number}</td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{p.customer_name || "—"}</td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{p.method}{p.reference ? ` · ${p.reference}` : ""}</td>
                  <td className="px-5 py-3.5 text-right font-semibold text-emerald-600">{money(p.amount)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <ConfirmDelete onConfirm={() => remove(p.id)} title="Remove payment?" trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-payment-row-${p.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="record-payment-dialog">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Invoice *</Label>
              <Select value={form.invoice_id} onValueChange={pickInv}>
                <SelectTrigger className="mt-1.5" data-testid="payment-invoice-select"><SelectValue placeholder="Select an unpaid invoice" /></SelectTrigger>
                <SelectContent>{invs.map((i) => <SelectItem key={i.id} value={i.id}>{i.number} · {i.customer?.name} · {money(Math.max(i.total - (i.amount_paid || 0), 0))} due</SelectItem>)}</SelectContent>
              </Select>
              {invs.length === 0 && <p className="text-xs text-slate-400 mt-1.5">No outstanding invoices.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (R) *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="payment-amount-page" /></div>
              <div>
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="payment-method-page"><SelectValue /></SelectTrigger>
                  <SelectContent>{["EFT", "Cash", "Card", "Cheque", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" data-testid="payment-ref-page" /></div>
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="payment-date-page" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-payment-page">Record</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
